import type { PointGroup } from 'signature_pad';

/**
 * Shortest total ink path, in CSS pixels, that counts as a signature. A tap
 * draws a single point (length 0) and a slip a few pixels; a handwritten
 * signature on the pad runs to hundreds of pixels.
 */
export const MIN_SIGNATURE_STROKE_LENGTH_PX = 40;

/** RGBA pixels carry 4 channels; alpha is the last one. */
const RGBA_CHANNELS = 4;
const ALPHA_OFFSET = 3;

/** Bounding box of the inked pixels of a canvas, in canvas pixels. */
export type InkBounds = {
  x: number;
  y: number;
  width: number;
  height: number;
};

/**
 * Total length of the strokes drawn on the pad: the sum of the distances
 * between consecutive points of every stroke.
 *
 * @param pointGroups - The pad's strokes (`SignaturePad#toData()`).
 * @returns The ink path length in CSS pixels.
 */
export function measureStrokeLength(pointGroups: PointGroup[]): number {
  let length = 0;
  for (const group of pointGroups) {
    let previous: { x: number; y: number } | null = null;
    for (const point of group.points) {
      if (previous) {
        length += Math.hypot(point.x - previous.x, point.y - previous.y);
      }
      previous = point;
    }
  }
  return length;
}

/**
 * Whether the pad holds an actual signature rather than nothing, a dot or an
 * accidental slip.
 *
 * @param pointGroups - The pad's strokes (`SignaturePad#toData()`).
 * @returns `true` when the ink path reaches {@link MIN_SIGNATURE_STROKE_LENGTH_PX}.
 */
export function isDrawnSignature(pointGroups: PointGroup[]): boolean {
  return measureStrokeLength(pointGroups) >= MIN_SIGNATURE_STROKE_LENGTH_PX;
}

/**
 * Smallest rectangle holding every non-transparent pixel of an RGBA buffer,
 * used to trim the exported signature to its ink.
 *
 * @param params.pixels - RGBA bytes, row by row (`ImageData#data`).
 * @param params.width - Width of the buffer in pixels.
 * @param params.height - Height of the buffer in pixels.
 * @returns The ink bounds, or `null` when every pixel is transparent.
 */
export function findInkBounds({
  pixels,
  width,
  height,
}: {
  pixels: Uint8ClampedArray;
  width: number;
  height: number;
}): InkBounds | null {
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const alpha = pixels[(y * width + x) * RGBA_CHANNELS + ALPHA_OFFSET];
      if (alpha === 0) {
        continue;
      }
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    }
  }
  if (maxX < 0) {
    return null;
  }
  return {
    x: minX,
    y: minY,
    width: maxX - minX + 1,
    height: maxY - minY + 1,
  };
}
